import { useMutation, useQueryClient } from 'react-query';
import { keyBy } from 'lodash';
import { VStack } from 'native-base';
import { FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UpdateEquipmentProfileRequest } from '@pimm/services/lib/sms-tenants';
import { UpdateEquipmentProfile } from '@pimm/services/lib/sms-tenants/services';
import { useToast } from '@app/hooks/toast.hook';
import { useStoreSettings } from '../context/store-settings.context';
import { transformAllEquipmentProfilesToFormData, useFormEquipmentProfile } from '../hooks/form-equipment-profile.hook';
import { EquipmentProfileCategoryCard } from './equipment-profile-category-card';
import Section from './section';

type EquipmentProfileViewProps = {
  name: string;
  title: string;
};

export const EquipmentProfileView = ({ name, title }: EquipmentProfileViewProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const equipmentProfiles = useStoreSettings(_ => _.queryEquipmentProfiles.data);
  const site = useStoreSettings(_ => _.site);
  const onConfigChange = useStoreSettings(_ => _.onConfigChange);
  const isEditEnabled = useStoreSettings(_ => _.isEquipmentEditable);
  const onSupportPress = useStoreSettings(_ => _.onSupportPress);

  const form = useFormEquipmentProfile({
    defaultValues: { categories: transformAllEquipmentProfilesToFormData(equipmentProfiles), isEditEnabled },
  });

  const updateEquipmentProfile = useMutation({
    mutationFn: (_inputs: UpdateEquipmentProfileRequest) => UpdateEquipmentProfile(_inputs),
    onError: () => {
      form.setError('root', { message: t('store_settings:equipment_profile_save_failed_banner') });
    },
    onSuccess: (_, _inputs) => {
      const mapInputsById = keyBy(_inputs.items, 'inventoryItemId');
      const nextEquipmentItems = equipmentProfiles?.map(_item => {
        const input = _item.inventoryItemId ? mapInputsById[_item.inventoryItemId] : undefined;
        const quantity = input?.quantity ?? _item.quantity;
        return { ..._item, isOverridden: input ? true : _item.isOverridden, quantity };
      });

      queryClient.setQueryData(['GetEquipmentProfile', { siteId: site.id }], nextEquipmentItems);

      onConfigChange({ ...site, lastModifiedOnUtc: new Date().toISOString() });

      toast.show({ type: 'success', message: t('store_settings:equipment_profile_updated_toast') });
      form.reset({
        categories: transformAllEquipmentProfilesToFormData(nextEquipmentItems),
        isEditEnabled,
      });
    },
  });

  const formCategories = useWatch({ control: form.control, name: 'categories' }) ?? [];
  const categories = formCategories
    .map((cat, index) => ({ ...cat, fieldIndex: index }))
    .filter(cat => cat?.subPage === name);
  const dirtyItems = formCategories.flatMap(cat => cat.items ?? []).filter(item => item.quantity !== item.value);
  const isDirty = dirtyItems.length > 0;
  const countDirtyFields = dirtyItems.length;

  const errorMessage = form.formState.errors['root']?.message;
  const hasLockedItems = categories.find(({ items }) => (items ?? []).some(_ => !_.isCustomerEditable));
  const supportMessage = hasLockedItems
    ? undefined
    : t('store_settings:equipment_profile_have_question', {
        page: t(title)?.toLowerCase() ?? '',
      });

  const handleSave = () => {
    if (updateEquipmentProfile.isLoading) return;

    form.clearErrors();

    const formValues = form.getValues();
    const inputs: UpdateEquipmentProfileRequest = {
      siteId: site.id,
      items: formValues.categories.flatMap(({ items }) =>
        (items ?? []).filter(_ => _.quantity !== _.value).map(_ => ({ inventoryItemId: _.inventoryItemId, quantity: _.value })),
      ),
    };

    updateEquipmentProfile.mutate(inputs);
  };

  return (
    <FormProvider {...form}>
      <Section
        title={t(title)}
        lastModified={site.lastModifiedOnUtc}
        _error={{ message: errorMessage, onDismiss: () => form.clearErrors('root') }}
        _supportLink={{ message: supportMessage, onPress: onSupportPress }}
        _scrollView={{ showsVerticalScrollIndicator: false }}
        _formActionBar={
          isDirty
            ? {
                countDirtyFields: countDirtyFields,
                isLoading: updateEquipmentProfile.isLoading,
                onCancel: () => form.reset(),
                onSave: form.handleSubmit(handleSave),
              }
            : undefined
        }
      >
        <VStack space={3} p={4} w="full" maxW={720} alignSelf="center">
          {categories.map(({ fieldIndex, categoryId }) => (
            <EquipmentProfileCategoryCard
              key={categoryId}
              fieldPath={`categories.${fieldIndex}`}
              isDisabled={updateEquipmentProfile.isLoading}
            />
          ))}
        </VStack>
      </Section>
    </FormProvider>
  );
};
